import type {
  SellarDistributionAvailabilityDistributionDeliveryEstimateTimetableListResponseType,
  SellarDistributionAvailabilityDistributionDeliveryEstimateTimetableListParametersType,
} from "@sellar-io/api-v2/types";
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  apiV2Request,
  getDefaultQueryOptions,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

/**
 * Fetches cached delivery-estimate timetable entries for a paginated list of
 * suppliers, firing one batched request per page rather than one request per
 * page × supplier. Query keys are scoped to each page's own supplier IDs, so
 * loading a new page never causes already-loaded pages to refetch.
 */
export const useSellarDistributionAvailabilityDistributionDeliveryEstimateTimetableList =
  ({
    supplierIdsByPage,
    buyerPostcode,
    enabled = true,
  }: {
    supplierIdsByPage: number[][];
    buyerPostcode: string;
    enabled?: boolean;
  }) => {
    const { business } = useSellarCurrentBusiness();
    const { user } = useFirebaseUser();

    const pageQueries = useQueries({
      queries: supplierIdsByPage.map((supplierIds) => {
        const params: SellarDistributionAvailabilityDistributionDeliveryEstimateTimetableListParametersType =
          {
            query: [
              {
                type: "supplierIds",
                modifier: "IN",
                value: supplierIds,
              },
              {
                type: "retailerPostcode",
                modifier: "EQUALS",
                value: buyerPostcode,
              },
            ],
          };

        return {
          staleTime: 30 * 60 * 1000, // 30 minutes
          queryKey: [
            user?.uid,
            business?.id,
            ServerStateKeys.SellarDistributionAvailabilityDistributionDeliveryEstimateTimetableList,
            params,
          ],
          queryFn: () =>
            apiV2Request({
              user,
              business,
              method: "get",
              path: `sellar-distribution-availability/delivery-estimate-timetable`,
              params: {
                ...params,
              },
            }).then(
              (data) =>
                apiV2AssertResponse<SellarDistributionAvailabilityDistributionDeliveryEstimateTimetableListResponseType>(
                  data,
                ).data,
            ),
          ...getDefaultQueryOptions({
            enabled,
          }),
        };
      }),
    });

    const data = useMemo(
      () => pageQueries.flatMap((query) => query.data ?? []),
      [pageQueries],
    );

    return {
      data,
      isLoading: pageQueries.some((query) => query.isLoading),
      isFetching: pageQueries.some((query) => query.isFetching),
    };
  };
